import axios from 'axios';
import { NavCategory } from '../enums/NavCategory.enum';
import { Color } from '../enums/Color.enum';
import { Fit } from '../enums/Fit.enum';
import { DataLayerEvent, DataLayerProduct, DataLayerVariant, ProductPayload } from '../models/DataLayer.model';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { Shopify } from '../models/Shopify.model';
import { ShopifyCheckout } from '../models/ShopifyCheckout.model';
import { ShopifyLineItem } from '../models/ShopifyLineItem.model';
import { ViewProduct } from '../models/ViewProduct.model';
import { LongSize } from '../product_detail/components/size.enum';
import {
  productClicked,
  productViewed,
  productDetailViewed,
  cartItemAdded,
  cartItemRemoved,
  sizingToolStarted,
  sizingToolCompleted,
  sizingToolStyleSelected,
  navigationLinkClicked,
  userDetected,
  reactPageLoaded,
  swimlaneLoaded,
  checkoutStepEncountered,
  referalPromoViewed,
  referalPromoClicked,
  cartPromoClicked,
  itemUpsellPromoViewed,
  desktopNavMenuOpened,
  mobileNavMenuOpened,
  videoEngaged,
  emailSubscriptionStarted,
  emailSubscriptionCompleted,
  purchase,
  userSignedIn,
  userSignedOut,
  userRegistered,
  outOfStockRedirectViewed,
  outOfStockRedirectClicked,
  checkoutValidationErrorEncountered,
  viewItInMySizeToolOpened,
  productColorChanged,
  productSizeChanged,
  productStyleChanged,
  formInteraction,
  paymentGatewayUsed,
  productImageViewed,
  purchaseRefererTest,
  purchaseTimingTest,
  purchaseDualTest,
  sizingToolUserResponseEvent,
  postPurchaseUserResponseEvent,
  quizToolUserResponseEvent,
  productListViewed,
} from './dataLayerEvents';

declare const window: HoneyloveWindow;

export const trackProductClicked = (shopify: Shopify | undefined, product: ViewProduct): void =>
  pushEvent(productClicked(shopify, window?.location.pathname, product));

export const trackProductDetailViewed = (
  shopify: Shopify | undefined,
  product: ViewProduct,
  variant: DataLayerVariant | undefined,
  color: Color | undefined,
  size: LongSize | undefined,
): void => {
  if (typeof window !== 'undefined') {
    pushEvent(productDetailViewed(shopify, window.location.pathname, product, variant, color, size));
  }
};

export const trackProductListViewed = (shopify: Shopify | undefined, products: ViewProduct[], list: string): void => {
  pushEvent(productListViewed(shopify, products, list));
};

export const trackLineItemListViewed = (shopify: Shopify | undefined, lineItems: ShopifyLineItem[], list: string): void => {
  const products = lineItems.map((lineItem) => lineItemToProduct(lineItem, list));
  pushEvent(productListViewed(shopify, products, list));
};

export const trackProductColorChanged = (
  shopify: Shopify | undefined,
  product: ViewProduct,
  variant: DataLayerVariant | undefined,
  color: Color,
  size?: LongSize,
): void => {
  pushEvent(productColorChanged(shopify, product, variant, color, size));
};

export const trackProductSizeChanged = (
  shopify: Shopify | undefined,
  product: ViewProduct,
  variant: DataLayerVariant | undefined,
  color: Color | undefined,
  size: LongSize | undefined,
  fit: Fit | undefined,
): void => {
  pushEvent(productSizeChanged(shopify, product, variant, color, size, fit));
};

export const trackProductStyleChanged = (
  shopify: Shopify | undefined,
  product: ViewProduct,
  variant: DataLayerVariant | undefined,
  color: Color,
  size?: LongSize,
): void => {
  pushEvent(productStyleChanged(shopify, product, variant, color, size));
};

export const trackSizingToolUserResponse = (response: Pick<DataLayerEvent, 'sizingToolUserResponse' | 'handle'>): void => {
  pushEvent(sizingToolUserResponseEvent(response));
};

export const trackQuizToolUserResponse = (response: Pick<DataLayerEvent, 'quizToolUserResponse' | 'handle'>): void => {
  pushEvent(quizToolUserResponseEvent(response));
};

export const trackPostPurchaseUserResponse = (response: Pick<DataLayerEvent, 'postPurchaseUserResponse'>): void => {
  pushEvent(postPurchaseUserResponseEvent(response));
};

export const trackProductViewed = (shopify: Shopify | undefined, productList: ProductPayload[]): void => pushEvent(productViewed(shopify, productList));

export const trackLineItemProductView = (shopify: Shopify | undefined, lineItems: ShopifyLineItem[], list: string): void => {
  const productList = lineItems.map((lineItem) => ({
    product: lineItemToProduct(lineItem, list),
    variant: lineItemToVariant(lineItem),
  }));
  trackProductViewed(shopify, productList);
};

export const trackCartItemAdded = (shopify: Shopify | undefined, product: DataLayerProduct, variant: DataLayerVariant, quantity: number): void =>
  pushEvent(cartItemAdded(shopify, window.location.pathname, product, variant, quantity));

export const trackCartItemRemoved = (shopify: Shopify | undefined, product: DataLayerProduct, variant: DataLayerVariant): void =>
  pushEvent(cartItemRemoved(shopify, window.location.pathname, product, variant));

export const trackSizingToolStarted = (): void => {
  pushEvent(sizingToolStarted());
};

export const trackSizingToolCompleted = (): void => {
  pushEvent(sizingToolCompleted());
};

export const trackSizingToolStyleSelected = (product: ViewProduct): void => {
  pushEvent(sizingToolStyleSelected(product));
};

export const trackViewItInMySizeToolOpened = (): void => pushEvent(viewItInMySizeToolOpened());

export const trackNavigationLinkClicked = (label?: string, location?: string): void => {
  pushEvent(navigationLinkClicked(label, location));
};

export const trackUserDetected = (userId: string, userEmail: string): void => pushEvent(userDetected(userId, userEmail));

export const trackReactPageLoaded = (pageContext: string | undefined): void => pushEvent(reactPageLoaded(pageContext ?? ''));

export const trackSwimlaneLoaded = (lane: number, label?: string): void => pushEvent(swimlaneLoaded(lane, label));

export const trackCheckoutValidationErrorEncountered = (form: string, field: string): void => pushEvent(checkoutValidationErrorEncountered(form, field));
export const trackCheckoutStepEncountered = (shopify: Shopify | undefined, productList: ProductPayload[], step: string, option: string): void =>
  pushEvent(checkoutStepEncountered(shopify, productList, step, option));
export const trackFormInteraction = (form: string, field: string): void => pushEvent(formInteraction(form, field));

export const trackOutOfStockRedirectViewed = (): void => pushEvent(outOfStockRedirectViewed());

export const trackOutOfStockRedirectClicked = (): void => pushEvent(outOfStockRedirectClicked());

export const trackReferralPromoViewed = (): void => pushEvent(referalPromoViewed());

export const trackReferralPromoClicked = (): void => pushEvent(referalPromoClicked());

export const trackItemUpsellPromoViewed = (): void => pushEvent(itemUpsellPromoViewed());

export const trackCartPromoClicked = (): void => pushEvent(cartPromoClicked());

export const trackDesktopNavMenuOpened = (label?: string): void => pushEvent(desktopNavMenuOpened(label));
export const trackMobileNavMenuOpened = (): void => pushEvent(mobileNavMenuOpened());

export const trackVideoEngaged = (): void => pushEvent(videoEngaged());

export const trackEmailSubscriptionStarted = (): void => pushEvent(emailSubscriptionStarted());

export const trackEmailSubscriptionCompleted = (): void => pushEvent(emailSubscriptionCompleted());

export const trackPurchase = (shopifyCheckout: ShopifyCheckout, productSets: ProductPayload[]): void => {
  pushEvent(purchase(shopifyCheckout, productSets));
};

export const trackPaymentGatewayUsed = (gateway: string): void => pushEvent(paymentGatewayUsed(gateway));

export const trackUserSignedIn = (): void => pushEvent(userSignedIn());

export const trackUserSignedOut = (): void => pushEvent(userSignedOut());

export const trackUserRegistered = (): void => pushEvent(userRegistered());

export const trackProductImageViewed = (title: string): void => pushEvent(productImageViewed(title));

export const trackCartLineItemAdded = (shopify: Shopify | undefined, lineItem: ShopifyLineItem): void => {
  const product = lineItemToProduct(lineItem);
  const variant = lineItemToVariant(lineItem);
  trackCartItemAdded(shopify, product, variant, 1);
};

export const trackPurchaseRefererTest = (transactionId: string): void => pushEvent(purchaseRefererTest(transactionId));
export const trackPurchaseTimingTest = (transactionId: string): void => pushEvent(purchaseTimingTest(transactionId));
export const trackPurchaseDualTest = (transactionId: string): void => pushEvent(purchaseDualTest(transactionId));

export const trackCartLineItemRemoved = (shopify: Shopify | undefined, lineItem: ShopifyLineItem): void => {
  const product = lineItemToProduct(lineItem);
  const variant = lineItemToVariant(lineItem);
  trackCartItemRemoved(shopify, product, variant);
};

export const lineItemToProduct = (item: ShopifyLineItem, list?: string): DataLayerProduct => ({
  id: item.product_id,
  title: item.product_title,
  sku: item.sku?.split('-')[0],
  price: item.price,
  vendor: item.vendor,
  type: item.product_type,
  list,
});

export const lineItemToVariant = (item: ShopifyLineItem): DataLayerVariant => ({
  title: item.variant_title,
  color: item.variant_options[0] as Color,
  quantity: item.quantity,
  size: item.variant_options[1] as LongSize,
  sku: item.sku,
  id: item.variant_id,
});

// Math.random should be unique because of its seeding algorithm.
// Convert it to base 36 (numbers + letters), and grab the first 9 characters
// after the decimal.
const eventID = () => Date.now() + '_' + Math.random().toString(36).substr(2, 9);

const pushEvent = (payload: DataLayerEvent): void => {
  if (typeof window !== 'undefined') {
    // note: we hash email on the server
    const augmentedEvent = {
      ...payload,
      eventID: eventID(),
      email: payload.email,
      gaClientId: window.ga?.getAll?.()?.[0]?.get('clientId'),
      gaSessionId: window.ga?.getAll?.()?.[1]?.get('dimension6'),
      eventSourceUrl: window?.document.location.href,
    };
    window.dataLayer = window.dataLayer || [];
    // add an event ID to all items for FB serverside linkage
    window.dataLayer.push(augmentedEvent);

    const streamEvents = [
      'NavigationLinkClicked', // CustomEvent
      'UserDetected', // CustomEvent
      'ProductDetailViewed', // ViewContent
      'ProductColorChanged', // CustomizeProduct
      'ProductSizeChanged', // CustomizeProduct
      'SizingToolProductSelected', // CustomizeProduct
      'CartItemAdded', // AddToCart
      'CheckoutStepEncountered', // step: 1 = InitiateCheckout
      'Purchase', // Purchase && AddPaymentInfo
      'SizingToolResponse', // Sizing Tool Responses
      'QuizToolResponse', // Quiz Tool Responses
      'PostPurchaseResponse', // Post Purchase Survey Responses
    ];

    const url = `https://${window.Honeylove.environment.dataApiDomain}/api/stream`;
    if (streamEvents.includes(payload.event)) {
      axios.post(url, augmentedEvent, { withCredentials: true }).catch((error) => {
        window.Sentry.captureException(error);
      });
    }
  }
};
